import { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { MdAdd as AddIcon, MdEdit as EditIcon, MdDelete as DeleteIcon, MdInfo as InfoIcon, MdLabel as LabelIcon } from 'react-icons/md';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import AddLabelRuleModal from './AddLabelRuleModal';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import LabelRegistryService from '@Helpers/URLHelpers/WeboutService/views/LabelRegistryService';
import ErrorHandler from '@Root/ErrorHandler';
import './LabelMatrixForm.scss';

const cn = 'labelMatrixForm';

export default function LabelMatrixForm(props) {

    const [cursor, setCursor] = useState('auto');
    const [formFieldList, setFormFieldList] = useState([]);
    const [isEdited] = useState(false);
    const [ruleTypes] = useState(['reporter', 'field']);
    const [fieldParameter, setFieldParameter] = useState([]);
    const [conditions] = useState(['EQUALS', 'NOT EQUALS', 'CONTAINS']);
    const [comparisonOperators] = useState(['EQUALS', 'GRATER THEN', 'LESS THEN']);
    const [property] = useState(['fullName', 'email', 'phoneNumber', 'domain']);
    const [labelList, setLabelList] = useState([]);
    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState({});
    const [rules, setRules] = useState([]);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [showAddRuleModal, setShowAddRuleModal] = useState(false);
    const [showEditRuleModal, setShowEditRuleModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [tempModalRule, setTempModalRule] = useState(null);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [validationError, setValidationError] = useState('');
    const [labelSearchTerm, setLabelSearchTerm] = useState('');

    const { currentProjectId } = useReduxProject();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const labelRegistryService = new LabelRegistryService();
    const errorHandler = new ErrorHandler();

    const filteredLabelList = labelList.filter((label) =>
        (label?.name || '').toLowerCase().includes(labelSearchTerm.toLowerCase())
    );

    useEffect(() => { renderData(); }, []);

    const renderData = async () => {
        try {
            setCursor('wait');
            if (!!props.formFieldList) {
                let formFieldsTemp = props.formFieldList;
                let formIdList = props.formFieldIdList;
                let formFields = [];
                let fieldParameterList = [];
                for (let i = 0; i < formFieldsTemp.length; i++) {
                    let itemObj = formFieldsTemp[i];
                    if (formIdList.includes(itemObj.id)) {
                        itemObj = { ...itemObj, options: JSON.parse(itemObj.options) };
                        formFields.push(itemObj);
                        fieldParameterList.push({ id: itemObj.id, type: itemObj.type, title: itemObj.title });
                    }
                }
                setFieldParameter(fieldParameterList);
                setFormFieldList(formFields);
                await renderKaiziqResolveConnection();
                await renderLabelList();
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const renderLabelList = async () => {
        for (let i = 0; i < 10; i++) {
            try {
                let list = await labelRegistryService.getListByProject(currentProjectId);
                list = JSON.parse(list.body);
                setLabelList(list);
                return true;
            } catch (err) {
                errorHandler.logError('Label List', err);
                if (i === 9) { errorHandler.handleError(err); return false; }
            }
        }
    };

    const renderKaiziqResolveConnection = async () => {
        try {
            let obj = await kaiziqResolveConnectionService.getKaiziqResolveConnectionById(props.kaiziqResolveConnectionId);
            obj = JSON.parse(obj.body);
            obj = { ...obj, details: JSON.parse(obj.details) };
            setKaiziqResolveConnection(obj);
            setRules(obj.details?.labelRules ?? []);
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validateDomain = (domain) => /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/.test(domain);
    const validatePhoneNumber = (phone) => /^\+?[\d\s\-()]{10,}$/.test(phone);

    const validateByPropertyAndCondition = (property, condition, value) => {
        if (!value) return '';
        if (property === 'email') {
            if (condition === 'EQUALS' || condition === 'NOT EQUALS') {
                if (!validateEmail(value)) return 'Enter a full email address (e.g. user@example.com)';
            } else if (condition === 'DOMAIN') {
                if (!validateDomain(value)) return 'Enter a domain only (e.g. example.com)';
            }
            // CONTAINS → any substring, no format restriction
        }
        if (property === 'phoneNumber' && !validatePhoneNumber(value)) {
            return 'Please enter a valid phone number';
        }
        return '';
    };

    const getAvailableConditions = () => {
        if (tempModalRule?.property === 'email') return [...conditions, 'DOMAIN'];
        return conditions;
    };

    const saveRuleToDatabase = async (updatedRules) => {
        try {
            let details = kaiziqResolveConnection.details;
            details.labelRules = updatedRules;
            await kaiziqResolveConnectionService.updateKaiziqResolveConnectionDetails(
                kaiziqResolveConnection?.id, JSON.stringify(details)
            );
        } catch (err) {
            errorHandler.handleError(err);
            showSnack('Error in updating rules', 'error');
        }
    };

    const validateModalRule = (rule) => {
        const error = validateByPropertyAndCondition(rule.property, rule.condition, rule.paramValue);
        if (error) { setValidationError(error); return false; }
        return true;
    };

    const handleOpenAddRuleModal = () => {
        setTempModalRule({ id: Date.now(), type: 'reporter', property: '', label: { rn: 0 }, fieldId: 0, condition: '', paramValue: '' });
        setValidationError('');
        setShowAddRuleModal(true);
    };

    const handleCloseAddRuleModal = () => { setShowAddRuleModal(false); setTempModalRule(null); setValidationError(''); };
    const handleOpenEditRuleModal = (rule) => { setTempModalRule({ ...rule }); setValidationError(''); setShowEditRuleModal(true); };
    const handleCloseEditRuleModal = () => { setShowEditRuleModal(false); setTempModalRule(null); setValidationError(''); };
    const handleOpenDeleteConfirmModal = (ruleId) => { setRuleToDelete(ruleId); setShowDeleteConfirmModal(true); };
    const handleCloseDeleteConfirmModal = () => { setRuleToDelete(null); setShowDeleteConfirmModal(false); };

    const handleConfirmDelete = async () => {
        const updatedRules = rules.filter(r => r.id !== ruleToDelete);
        setRules(updatedRules);
        setShowDeleteConfirmModal(false);
        setRuleToDelete(null);
        showSnack('Rule deleted successfully', 'success');
        await saveRuleToDatabase(updatedRules);
    };

    const handleAddRuleSave = async (newRule) => {
        if (!validateModalRule(newRule)) return;
        const updatedRules = [...rules, newRule];
        setRules(updatedRules);
        setShowAddRuleModal(false);
        setValidationError('');
        showSnack('Rule added successfully', 'success');
        await saveRuleToDatabase(updatedRules);
    };

    const handleEditRuleSave = async (updatedRule) => {
        if (!validateModalRule(updatedRule)) return;
        const updatedRules = rules.map(r => r.id === updatedRule.id ? updatedRule : r);
        setRules(updatedRules);
        setShowEditRuleModal(false);
        setValidationError('');
        showSnack('Rule updated successfully', 'success');
        await saveRuleToDatabase(updatedRules);
    };

    const handleModalRuleChange = (field, value) => {
        setTempModalRule(prev => ({ ...prev, [field]: value }));
        if (field === 'paramValue') {
            const error = validateByPropertyAndCondition(tempModalRule?.property, tempModalRule?.condition, value);
            setValidationError(error);
        }
    };

    const handleModalRuleFieldChange = (fieldId) => {
        const selectedField = fieldParameter.find(f => f.id === fieldId);
        setTempModalRule(prev => ({ ...prev, fieldId, property: selectedField?.type || '', condition: '', paramValue: '' }));
    };

    const handleModalRuleLabelChange = (selectedLabelRn) => {
        const selectedLabel = labelList.find(l => l.rn === selectedLabelRn);
        setTempModalRule(prev => ({
            ...prev,
            label: { name: selectedLabel.name, id: selectedLabel.id, type: selectedLabel.type, rn: selectedLabel.rn, projectId: currentProjectId }
        }));
    };

    const handleModalRuleDurationChange = (durationType, value) => {
        setTempModalRule(prev => ({ ...prev, paramValue: { ...prev.paramValue, [durationType]: value } }));
    };

    const showSnack = (message, type) => {
        setSnackBarMessage(message);
        setSnackBarType(type);
        setSnackBarOpen(true);
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackBarOpen(false);
    };

    const getRulesSummary = (rule) => {
        const labelName = rule.label?.name || 'Select Label';
        const conditionText = rule.condition?.replace('_', ' ') || '';

        if (rule.type === 'reporter') {
            const propertyLabel = rule.property === 'fullName' ? 'Full Name' :
                                  rule.property === 'phoneNumber' ? 'Phone Number' :
                                  rule.property?.toUpperCase() || 'N/A';
            return (
                <span className={`${cn}__ruleSummary`}>
                    <span className={`${cn}__ruleSummary__source`}>Reporter</span>
                    <span className={`${cn}__ruleSummary__prop`}>{propertyLabel}</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--condition`}>{conditionText}</span>
                    <span className={`${cn}__ruleSummary__value`}>"{rule.paramValue}"</span>
                    <span className={`${cn}__ruleSummary__arrow`}>→</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--label`}>{labelName}</span>
                </span>
            );
        } else if (rule.type === 'field') {
            const field = formFieldList.find(f => f.id === rule.fieldId);
            return (
                <span className={`${cn}__ruleSummary`}>
                    <span className={`${cn}__ruleSummary__source`}>Field</span>
                    <span className={`${cn}__ruleSummary__prop`}>{field?.title || 'Select Field'}</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--condition`}>{conditionText}</span>
                    <span className={`${cn}__ruleSummary__value`}>{rule.paramValue}</span>
                    <span className={`${cn}__ruleSummary__arrow`}>→</span>
                    <span className={`${cn}__ruleSummary__badge ${cn}__ruleSummary__badge--label`}>{labelName}</span>
                </span>
            );
        }
        return 'Incomplete Rule';
    };

    const sharedModalProps = {
        rule: tempModalRule,
        onRuleChange: handleModalRuleChange,
        onFieldChange: handleModalRuleFieldChange,
        onLabelChange: handleModalRuleLabelChange,
        onDurationChange: handleModalRuleDurationChange,
        validationError,
        ruleTypes,
        conditions,
        comparisonOperators,
        property,
        fieldParameter,
        formFieldList,
        filteredLabelList,
        labelSearchTerm,
        setLabelSearchTerm,
        getAvailableConditions,
        saveDisabled: !tempModalRule?.label?.rn || !tempModalRule?.condition || !!validationError,
    };

    return (
        <div className={cn} style={{ cursor }}>

            {/* ── Header ───────────────────────────────────────────────────── */}
            <div className={`${cn}__header`}>
                <div className={`${cn}__header__left`}>
                    <div className={`${cn}__header__iconWrap`}>
                        <LabelIcon />
                    </div>
                    <div className={`${cn}__header__text`}>
                        <Badge color='error' variant='dot' invisible={!isEdited}>
                            <h2 className={`${cn}__header__title`}>Ticket Label Matrix</h2>
                        </Badge>
                        <p className={`${cn}__header__subtitle`}>
                            Rules are evaluated top-to-bottom — the last match assigns the label.
                        </p>
                    </div>
                </div>
                <Tooltip
                    title={<span>Rules are evaluated from top to bottom.<br />The last matching rule assigns the label.</span>}
                    slots={{ transition: Zoom }}
                    arrow>
                    <span className={`${cn}__header__infoIcon`}>
                        <InfoIcon />
                    </span>
                </Tooltip>
            </div>

            {/* ── Rules list ────────────────────────────────────────────────── */}
            <div className={`${cn}__body`}>
                {rules.length === 0 ? (
                    <div className={`${cn}__empty`}>
                        <LabelIcon className={`${cn}__empty__icon`} />
                        <p className={`${cn}__empty__text`}>No rules configured yet.</p>
                        <p className={`${cn}__empty__hint`}>Add a rule to automatically label tickets based on reporter or field values.</p>
                    </div>
                ) : (
                    <div className={`${cn}__rulesList`}>
                        {rules.map((rule, index) => (
                            <div className={`${cn}__ruleItem`} key={rule.id}>
                                <div className={`${cn}__ruleItem__index`}>{index + 1}</div>
                                <div className={`${cn}__ruleItem__summary`}>
                                    {getRulesSummary(rule)}
                                </div>
                                <div className={`${cn}__ruleItem__actions`}>
                                    <button
                                        className={`${cn}__ruleItem__btn ${cn}__ruleItem__btn--edit`}
                                        onClick={() => handleOpenEditRuleModal(rule)}
                                        title='Edit Rule'>
                                        <EditIcon />
                                    </button>
                                    <button
                                        className={`${cn}__ruleItem__btn ${cn}__ruleItem__btn--delete`}
                                        onClick={() => handleOpenDeleteConfirmModal(rule.id)}
                                        title='Delete Rule'>
                                        <DeleteIcon />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button className={`${cn}__addBtn`} onClick={handleOpenAddRuleModal}>
                    <AddIcon className={`${cn}__addBtn__icon`} />
                    Add Rule
                </button>
            </div>

            {/* ── Add Rule Modal ────────────────────────────────────────────── */}
            <AddLabelRuleModal
                open={showAddRuleModal}
                onClose={handleCloseAddRuleModal}
                onSave={handleAddRuleSave}
                title='Add New Rule'
                {...sharedModalProps}
            />

            {/* ── Edit Rule Modal ───────────────────────────────────────────── */}
            <AddLabelRuleModal
                open={showEditRuleModal}
                onClose={handleCloseEditRuleModal}
                onSave={handleEditRuleSave}
                title='Edit Rule'
                {...sharedModalProps}
            />

            {/* ── Delete Confirmation Modal ─────────────────────────────────── */}
            <Dialog
                open={showDeleteConfirmModal}
                onClose={handleCloseDeleteConfirmModal}
                maxWidth='xs'
                fullWidth
                className={`${cn}__deleteDialog`}>
                <DialogTitle className={`${cn}__deleteDialog__title`}>
                    Delete Rule
                </DialogTitle>
                <DialogContent className={`${cn}__deleteDialog__content`}>
                    <p className={`${cn}__deleteDialog__text`}>
                        Are you sure you want to delete this rule? This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions className={`${cn}__deleteDialog__actions`}>
                    <button className={`${cn}__cancelBtn`} onClick={handleCloseDeleteConfirmModal}>
                        Cancel
                    </button>
                    <BorderBlueButtonV2 text='Delete' onClick={handleConfirmDelete} />
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ──────────────────────────────────────────────────── */}
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}>
                <Alert onClose={handleSnackBarClose} severity={snackBarType} variant='filled' sx={{ width: '100%' }}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
